import {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {
  Alert,
  Button,
  Input,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Tabs,
  Typography,
  Upload,
  Checkbox,
  DatePicker,
} from 'antd';

import {CreateAssetBlock} from '../../components';
import {Asset, AssetType, ProgramType, QUESTION_TYPE} from '../../graphql/API';
import type {TestDraft} from '../Questions/QuestionTypes';
import {PlacementTestsService} from './PlacementTestsService';
import {validateCreateTest} from '../../utils/Validators';
import {BackendQuestionType} from '../../types/newQuestionType';
import {CloseCircleFilled, UploadOutlined} from '@ant-design/icons';
import {UploadFile, UploadProps} from 'antd/lib/upload/interface';
import {StorageService, UploadFileType} from '../Storage/StorageService';
import {CreateAssetType} from '../../components/CreateAssetType';
import {CreateAssetUpload} from '../../components/CreateAssetUpload';
import {QUESTION_TYPE_DICT} from '../Test/QuestionTypeDict';
import {ProgramsNewService} from '../Program/ProgramsNewService';
import {
  duplicateChoicesKeywordsByWordCount,
  replaceChoicesInText,
  detectBlankKeywords,
  replaceBlankKeywordsInText,
  duplicateBlankKeywordsByWordCount,
} from '../Test/utils/comprehensionParser';
import {getQuestionStaticValues} from '../../utils/staticValuesUtils/questionStaticValues';
import {getPlacementTestStaticDescription} from '../../utils/staticValuesUtils/placementTestStaticValues';
import {
  getLevelDeterminationAllowedQuestionTypesText,
  isLevelDeterminationPlacementTest,
  isQuestionTypeAllowedForLevelDetermination,
  PLACEMENT_TEST_TYPE,
} from './placementTestTypes';
import {RearrangeSentenceEditor} from '../Test/components/RearrangeSentenceEditor';

const placementTestService = new PlacementTestsService();
const storageService = new StorageService();
const programsNewService = new ProgramsNewService();

export const PlacementTestCreate = () => {
  const history = useHistory();
  const {Title, Text} = Typography;
  const {TabPane} = Tabs;

  // Get initial static values for default question
  const initialValues = getQuestionStaticValues(
    QUESTION_TYPE.READ_SENTENCE,
    'PHOTO',
  );

  const emptyTestPane = {
    name: '',
    //@ts-ignore
    correctAsset: {
      type: AssetType.AUDIO,
      thumbnailUri: '',
      fileUri: '',
    },
    //@ts-ignore
    asset: {
      type: AssetType.PHOTO,
      thumbnailUri: '',
      fileUri: '',
    },
    programType: ProgramType.LANGUAGE,
    summary: initialValues.summary,
    description: initialValues.description,
    text: '',
    textAI: '',
    originalTextAI: '',
    correctIndex: {},
    choices: [],
    choicesCount: {},
    type: QUESTION_TYPE.READ_SENTENCE,
    program: '', // NEW: Program ID for the question
    difficulty: 'NORMAL', // NEW: Difficulty level (EASY, NORMAL, HARD)
    // NEW: Payload structure for COMPREHENSION, FILL_IN_THE_BLANKS, and REARRANGE_SENTENCE
    payload: {
      blanks: [],
      words: [],
      shuffledOrder: [],
      correctOrder: [],
      media: [],
    },
  };

  //@ts-ignore
  const [panes, setPanes] = useState<any[]>([emptyTestPane]);
  const [activeKey, setActiveKey] = useState<any>(0);

  const [placementTestName, setPlacementTestName] = useState<string>('');
  const [placementTestType, setPlacementTestType] = useState<string>('');
  const [programsList, setProgramsList] = useState<any[]>([]); // NEW: Programs list

  const [fileAudio, setFileAudio] = useState<UploadFile>();

  const [uploading, setUploading] = useState(false);
  const [selectChoice, setSelectChoice] = useState<string>('');

  // NEW: Placement test specific fields
  const [duration, setDuration] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [expirationDate, setExpirationDate] = useState<string>('');

  // @ts-ignore
  const [placementTestAsset, setPlacementTestAsset] = useState<Asset>({
    type: AssetType.VIDEO,
    thumbnailUri: '',
    fileUri: '',
  });
  const [placementTestDescription, setPlacementTestDescription] =
    useState<string>(getPlacementTestStaticDescription('VIDEO'));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [shownChoicesAlerts, setShownChoicesAlerts] = useState<Set<number>>(
    new Set(),
  );
  useEffect(() => {
    // Fetch programs list
    programsNewService
      .getNewPrograms()
      .then(programs => {
        console.log('programs_list', programs);
        setProgramsList(programs);

        // Auto-select first program for all panes if they don't have one
        if (programs.length > 0) {
          const firstProgramId = programs[0]._id || programs[0].id;
          setPanes(prevPanes =>
            prevPanes.map(pane => ({
              ...pane,
              program: pane.program || firstProgramId,
            })),
          );
          console.log('Auto-selected first program:', firstProgramId);
        }
      })
      .catch(error => {
        console.error('Error fetching programs:', error);
      });

    return () => {};
  }, []);

  const onChange = (activeKey: string) => {
    setActiveKey(parseInt(activeKey));
  };

  const onEdit = (targetKey: any, action: any) => {
    switch (action) {
      case 'add':
        add();
        break;
      default:
    }
  };

  const add = () => {
    const activeKey = panes.length;
    const newPanes = panes;

    // Create a fresh copy of emptyTestPane to avoid reference issues
    const freshPane = {
      ...emptyTestPane,
      asset: {...emptyTestPane.asset},
      correctAsset: {...emptyTestPane.correctAsset},
    };

    // @ts-ignore
    newPanes.push(freshPane);
    setPanes(newPanes);
    setActiveKey(activeKey);
  };
  const propsAudioUpload: UploadProps = {
    type: 'select',
    accept: '.aac',
    listType: 'text',
    multiple: false,
    maxCount: 1,
    onRemove: file => {
      setFileAudio(null);
    },
    beforeUpload: file => {
      setFileAudio(file);

      return false;
    },
  };

  const handleUpload = async (
    fileUri: UploadFile,
    typeUpload: UploadFileType,
    index: number,
  ) => {
    setUploading(true);
    try {
      let filePath: string;
      switch (typeUpload) {
        case UploadFileType.TEST_MEDIA:
          filePath = await storageService.uploadFile(
            fileUri,
            typeUpload,
            'test/media',
          );
          let tempAsset = {
            fileUri: filePath,
            type: AssetType.AUDIO,
            thumbnailUri: '',
          };
          let tempPanes = [...panes];
          // @ts-ignore
          tempPanes[index].correctAsset = tempAsset;
          setPanes(tempPanes);
          break;

        case UploadFileType.TEST_MEDIA_VIDEO_SESSION:
          filePath = await storageService.uploadFile(
            fileUri,
            typeUpload,
            'test/media',
          );
          let tempPaness = [...panes];
          // @ts-ignore
          tempPaness[index].videoAISessionUri = filePath;
          setPanes(tempPaness);
          break;

        default:
          break;
      }

      //console.log('file_PATH_IUPLOADED', filePath);
      message.success('upload successfully.');

      setUploading(false);
    } catch (error) {
      setUploading(false);
      message.error('upload failed.');
    }
  };

  const handlePlacementTestAssetTypeChange = asset => {
    // Set static description based on asset type
    const staticDescription = getPlacementTestStaticDescription(
      asset.type || 'DEFAULT',
    );
    setPlacementTestDescription(staticDescription);
    setPlacementTestAsset(asset);
  };
  const handleQuestionTypeChange = (
    type: string,
    asset: string,
    index?: number,
  ) => {
    const {summary, description} = getQuestionStaticValues(type, asset);

    // Update the panes array if index is provided
    if (index !== undefined) {
      let tempPanes = [...panes];
      tempPanes[index].summary = summary;
      tempPanes[index].description = description;

      // Initialize payload structure based on question type
      if (
        type === QUESTION_TYPE.COMPREHENSION ||
        (type as string) === 'FILL_IN_THE_BLANKS'
      ) {
        tempPanes[index].payload = {
          blanks: [],
          media: tempPanes[index].payload?.media || [],
        };
      } else if ((type as string) === 'REARRANGE_SENTENCE') {
        tempPanes[index].payload = {
          words: [],
          shuffledOrder: [],
          correctOrder: [],
          media: tempPanes[index].payload?.media || [],
        };
      } else {
        tempPanes[index].payload = {
          blanks: [],
          media: tempPanes[index].payload?.media || [],
        };
      }

      setPanes(tempPanes);
    }
  };

  function cleanStringForAI(input) {
    return input
      .toLowerCase() // Convert to lowercase
      .replace(/[^a-z0-9'\s]/g, ''); // Remove special characters and punctuation, except apostrophe
  }

  const initializeBlankOptions = (tempPanes: any[], index: number) => {
    const pane = tempPanes[index];
    const blankKeywords = detectBlankKeywords(
      pane.text || '',
      pane.textAI || '',
    );
    const currentChoices = pane.customChoices || {};

    const customChoices = blankKeywords.reduce((acc, blankKey) => {
      acc[blankKey] = currentChoices[blankKey]?.length
        ? [currentChoices[blankKey][0]]
        : [''];
      return acc;
    }, {});

    const choicesCount = blankKeywords.reduce((acc, blankKey) => {
      acc[blankKey] = 1;
      return acc;
    }, {});

    tempPanes[index].customChoices = customChoices;
    tempPanes[index].choicesCount = choicesCount;

    if (blankKeywords.length > 0) {
      setSelectChoice(prevChoice =>
        blankKeywords.includes(prevChoice) ? prevChoice : blankKeywords[0],
      );
    }

    return blankKeywords;
  };

  const buildBlankPayload = pane => {
    const customChoices = pane.customChoices || {};
    const correctIndex = pane.correctIndex || {};

    return Object.keys(customChoices).map(blankKey => {
      const choices = customChoices[blankKey] || [];
      const answerIndex = correctIndex[blankKey] ?? 0;

      return {
        name: blankKey,
        choices,
        correctAnswer: choices[answerIndex] || '',
      };
    });
  };

  const updateFillBlankDerivedFields = (tempPanes: any[], index: number) => {
    const pane = tempPanes[index];
    const originalText = pane.originalTextAI || pane.text || '';
    const blanks = buildBlankPayload(pane);

    tempPanes[index].text = duplicateBlankKeywordsByWordCount(
      originalText,
      blanks,
    );
    tempPanes[index].textAI = replaceBlankKeywordsInText(originalText, blanks);
    tempPanes[index].payload = {
      ...tempPanes[index].payload,
      blanks,
    };
  };

  const validateLevelDeterminationQuestions = () => {
    if (!isLevelDeterminationPlacementTest(placementTestType)) {
      return {isValid: true, errors: []};
    }

    const errors = panes
      .map((pane, index) =>
        isQuestionTypeAllowedForLevelDetermination(pane?.type)
          ? null
          : `question_${index}: ${pane?.type || 'missing type'}`,
      )
      .filter(Boolean) as string[];

    return {isValid: errors.length === 0, errors};
  };

  return (
    <>
      <div style={{padding: 10}}>
        <Title style={{textAlign: 'center'}}>Create Placement Test</Title>
        <>
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Placement Test name </Text>
            <Input
              onChange={e => {
                setPlacementTestName(e.target.value);
                let newPanes = [...panes];
                newPanes[0].name = e.target.value;
                setPanes(newPanes);
              }}
              placeholder="Placement Test name"
            />
          </div>
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text style={{display: 'block'}}>Placement Test type</Text>
            <Select
              allowClear
              value={placementTestType || undefined}
              style={{width: '100%'}}
              placeholder="General placement test"
              onChange={value => setPlacementTestType(value || '')}>
              <Select.Option value={PLACEMENT_TEST_TYPE.LEVEL_DETERMINATION}>
                Level Determination
              </Select.Option>
            </Select>
          </div>
          {isLevelDeterminationPlacementTest(placementTestType) && (
            <Alert
              type="info"
              showIcon
              style={{marginTop: '20px', marginBottom: '20px'}}
              message="Allowed questions for LEVEL_DETERMINATION"
              description={getLevelDeterminationAllowedQuestionTypesText()}
            />
          )}
          {/* NEW: Duration field */}
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Duration (e.g., "30 minutes", "1 hour") </Text>
            <Input
              value={duration}
              onChange={e => setDuration(e.target.value)}
              placeholder="e.g., 30 minutes"
            />
          </div>
          {/* NEW: Start Date field */}
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text style={{display: 'block'}}>Start Date </Text>
            <DatePicker
              style={{width: '100%'}}
              showTime
              onChange={date => {
                setStartDate(date ? date.toISOString() : '');
              }}
              placeholder="Select start date"
            />
          </div>
          {/* NEW: Expiration Date field */}
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text style={{display: 'block'}}>Expiration Date </Text>
            <DatePicker
              style={{width: '100%'}}
              showTime
              onChange={date => {
                setExpirationDate(date ? date.toISOString() : '');
              }}
              placeholder="Select expiration date"
            />
          </div>
          <CreateAssetType
            defaultAssetType={AssetType.VIDEO}
            // @ts-ignore TODO be checked
            onChange={selectedAsset =>
              handlePlacementTestAssetTypeChange(selectedAsset)
            }
          />
          <div style={{marginTop: '20px', marginBottom: '20px'}}>
            <Text>Placement Test description </Text>
            <Input
              value={getPlacementTestStaticDescription(
                placementTestAsset?.type || 'DEFAULT',
              )}
              onChange={e => {}}
              placeholder="Placement Test description"
              disabled
            />
          </div>

          <CreateAssetUpload
            defaultAssetType={placementTestAsset.type}
            asset={placementTestAsset}
            // @ts-ignore TODO be checked
            onChange={selectedAsset => setPlacementTestAsset(selectedAsset)}
          />
        </>

        <Tabs
          type="editable-card"
          onChange={onChange}
          activeKey={activeKey.toString()}
          onEdit={onEdit}>
          {panes?.map((pane, index) => (
            <TabPane
              tab={'question_' + index}
              key={index}
              closable={false}
              style={{
                border: '2px solid gray',
                padding: '10px',
              }}>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Question name</Text>
                <Input
                  onChange={e => {
                    let tempPanes = [...panes];
                    tempPanes[index].name = e.target.value;
                    setPanes(tempPanes);
                  }}
                  placeholder="Question name"
                  value={panes[index].name}
                />
              </div>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text style={{display: 'block'}}>Question type </Text>
                <Select
                  value={panes[index].type}
                  showSearch
                  style={{width: 300}}
                  placeholder="Select a question type"
                  optionFilterProp="children"
                  onChange={(selectedQuestionType: any) => {
                    let tempPanes = [...panes];
                    console.log('selectedQuestionType->', selectedQuestionType);
                    tempPanes[index].type = selectedQuestionType;
                    //@ts-ignore
                    tempPanes[index].customChoices = [];
                    setPanes(tempPanes);
                    handleQuestionTypeChange(
                      selectedQuestionType,
                      panes[index].asset.type,
                      index,
                    );
                  }}>
                  <Select.Option value={QUESTION_TYPE.READ_SENTENCE}>
                    {QUESTION_TYPE.READ_SENTENCE} (default)
                  </Select.Option>
                  {/*MUST ENTER 4 Choices*/}
                  <Select.Option
                    value={QUESTION_TYPE.CHOICES}
                    disabled={isLevelDeterminationPlacementTest(
                      placementTestType,
                    )}>
                    {QUESTION_TYPE.CHOICES}
                  </Select.Option>
                  {/*MUST ENTER TEXTAI*/}
                  <Select.Option value={QUESTION_TYPE.MISSING_LETTERS}>
                    {QUESTION_TYPE.MISSING_LETTERS}
                  </Select.Option>
                  <Select.Option value={QUESTION_TYPE.MISSING_WORD}>
                    {QUESTION_TYPE.MISSING_WORD}
                  </Select.Option>
                  {/*MUST ENTER 4 Choices*/}
                  <Select.Option
                    value={QUESTION_TYPE.COMPREHENSION}
                    disabled={isLevelDeterminationPlacementTest(
                      placementTestType,
                    )}>
                    {QUESTION_TYPE.COMPREHENSION}
                  </Select.Option>
                  {/* TRANSCRIBE_AND_SCORE */}
                  <Select.Option value={QUESTION_TYPE.TRANSCRIBE_AND_SCORE}>
                    {QUESTION_TYPE.TRANSCRIBE_AND_SCORE}
                  </Select.Option>
                  {/* DESCRIBE_IMAGE */}
                  <Select.Option value={BackendQuestionType.DESCRIBE_IMAGE}>
                    {BackendQuestionType.DESCRIBE_IMAGE}
                  </Select.Option>
                  {/* FREE_TEXT_ONLY_PRONUNCIATION */}
                  <Select.Option
                    value={QUESTION_TYPE.FREE_TEXT_ONLY_PRONUNCIATION}>
                    {QUESTION_TYPE.FREE_TEXT_ONLY_PRONUNCIATION}
                  </Select.Option>
                  {/* FILL_IN_THE_BLANKS */}
                  <Select.Option value={'FILL_IN_THE_BLANKS' as any}>
                    {'FILL_IN_THE_BLANKS' as any}
                  </Select.Option>
                  {/* REARRANGE_SENTENCE */}
                  <Select.Option value={'REARRANGE_SENTENCE' as any}>
                    {'REARRANGE_SENTENCE' as any}
                  </Select.Option>
                </Select>
              </div>
              {/* NEW: Program Dropdown */}
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Program *</Text>
                <Select
                  style={{width: '100%', marginTop: '5px'}}
                  placeholder="Select a program"
                  value={panes[index].program || undefined}
                  optionFilterProp="children"
                  onChange={(selectedProgramId: string) => {
                    let tempPanes = [...panes];
                    tempPanes[index].program = selectedProgramId;
                    setPanes(tempPanes);
                  }}>
                  {programsList.map((program: any, programIndex) => (
                    <Select.Option
                      value={program.id || program._id}
                      key={programIndex}>
                      {program.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              {/* NEW: Difficulty Selector */}
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Difficulty Level</Text>
                <Select
                  style={{width: '100%', marginTop: '5px'}}
                  placeholder="Select difficulty"
                  value={panes[index].difficulty || 'NORMAL'}
                  optionFilterProp="children"
                  onChange={(
                    selectedDifficulty: 'EASY' | 'NORMAL' | 'HARD',
                  ) => {
                    let tempPanes = [...panes];
                    tempPanes[index].difficulty = selectedDifficulty;
                    setPanes(tempPanes);
                  }}>
                  <Select.Option value="EASY">Easy</Select.Option>
                  <Select.Option value="NORMAL">Normal</Select.Option>
                  <Select.Option value="HARD">Hard</Select.Option>
                </Select>
              </div>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Question Summary</Text>
                <Input
                  value={
                    getQuestionStaticValues(
                      panes[index].type,
                      panes[index].asset.type,
                    ).summary
                  }
                  onChange={e => {}}
                  placeholder="Question Summary"
                  disabled
                />
              </div>
              <div style={{marginTop: '20px', marginBottom: '20px'}}>
                <Text>Question description</Text>
                <Input
                  value={
                    getQuestionStaticValues(
                      panes[index].type,
                      panes[index].asset.type,
                    ).description
                  }
                  onChange={e => {}}
                  placeholder="Question description"
                  disabled
                />
              </div>

              {pane.type === QUESTION_TYPE.CHOICES ? (
                <div>
                  {new Array(4).fill(null).map((_, arrayIndex) => {
                    return (
                      <div
                        key={arrayIndex}
                        style={{marginTop: '20px', marginBottom: '20px'}}>
                        <Text>Choice number {arrayIndex + 1}</Text>
                        <Input
                          value={panes[index].choices[arrayIndex]}
                          onChange={e => {
                            let tempPanes = [...panes];
                            //@ts-ignore
                            tempPanes[index].choices[arrayIndex] =
                              e.target.value;

                            setPanes(tempPanes);
                          }}
                          placeholder={`Choice number ${arrayIndex + 1}`}
                        />
                      </div>
                    );
                  })}
                  <div
                    style={{
                      marginTop: '20px',
                      marginBottom: '20px',
                      border: '2px solid red',
                    }}>
                    <Text>Question text</Text>
                    <Input
                      value={panes[index].text}
                      onChange={e => {
                        let tempPanes = [...panes];
                        tempPanes[index].text = e.target.value;
                        tempPanes[index].textAI = cleanStringForAI(
                          e.target.value,
                        );
                        setPanes(tempPanes);
                      }}
                      placeholder="Question text"
                    />
                  </div>
                </div>
              ) : (pane.type as string) === 'REARRANGE_SENTENCE' ? null : (
                <div style={{marginTop: '20px', marginBottom: '20px'}}>
                  <Text>Question text</Text>
                  <Input
                    value={panes[index].text}
                    onBlur={() => {
                      if (pane.type === QUESTION_TYPE.COMPREHENSION) {
                        // Check BOTH text and textAI fields for CHOICES keywords
                        const textChoices = (panes[index].text || '').split(
                          ' ',
                        );
                        const aiTextChoices = (panes[index].textAI || '').split(
                          ' ',
                        );
                        const allChoices = [...textChoices, ...aiTextChoices];

                        let newOptions = {};
                        let newChoicesCount = {};

                        // Find all CHOICES keywords from both fields
                        const numOfchoiceOptions = allChoices
                          .filter(text => text.startsWith('CHOICES'))
                          .map(text => text.replace(/[.,!?:;]+$/, '')); // Strip trailing punctuation

                        // Remove duplicates using Set
                        const uniqueChoiceOptions = [
                          ...new Set(numOfchoiceOptions),
                        ];

                        // Show alert only once if no CHOICES keywords found
                        if (uniqueChoiceOptions.length === 0) {
                          if (!shownChoicesAlerts.has(index)) {
                            alert(
                              'Must Text or AI Text Contains at least one keyword CHOICES1',
                            );
                            setShownChoicesAlerts(
                              new Set(shownChoicesAlerts).add(index),
                            );
                          }
                        } else {
                          // Remove from shown alerts if user has fixed the issue
                          if (shownChoicesAlerts.has(index)) {
                            const newAlerts = new Set(shownChoicesAlerts);
                            newAlerts.delete(index);
                            setShownChoicesAlerts(newAlerts);
                          }
                        }

                        // REMOVABLE: Debug logging
                        console.log(
                          'CHOICES keywords detected in text:',
                          textChoices.filter(t => t.startsWith('CHOICES')),
                        );
                        console.log(
                          'CHOICES keywords detected in textAI:',
                          aiTextChoices.filter(t => t.startsWith('CHOICES')),
                        );
                        console.log(
                          'All unique CHOICES keywords:',
                          uniqueChoiceOptions,
                        );

                        uniqueChoiceOptions.forEach(value => {
                          newOptions[value] = [];
                          newChoicesCount[value] = 2; // Initialize with 2 choices
                        });

                        let tempPanes = [...panes];
                        //@ts-ignore
                        tempPanes[index].customChoices = newOptions;
                        //@ts-ignore
                        tempPanes[index].choicesCount = newChoicesCount;

                        // REMOVABLE: Debug logging
                        console.log(
                          'Initialized choicesCount:',
                          newChoicesCount,
                        );

                        // Auto-select first CHOICES keyword
                        if (uniqueChoiceOptions.length > 0) {
                          setSelectChoice(uniqueChoiceOptions[0]);
                          // REMOVABLE: Debug logging
                          console.log(
                            'Auto-selected choice:',
                            uniqueChoiceOptions[0],
                          );
                        }

                        setPanes(tempPanes);
                      } else if (
                        (pane.type as string) === 'FILL_IN_THE_BLANKS'
                      ) {
                        let tempPanes = [...panes];
                        const blankKeywords = initializeBlankOptions(
                          tempPanes,
                          index,
                        );

                        if (blankKeywords.length === 0) {
                          alert(
                            'Must Text Contains at least one keyword BLANK1',
                          );
                        }

                        setPanes(tempPanes);
                      } else {
                        let tempPanes = [...panes];

                        //@ts-ignore
                        tempPanes[index].customChoices = {};
                        setPanes(tempPanes);
                      }
                    }}
                    onChange={e => {
                      let tempPanes = [...panes];
                      tempPanes[index].text = e.target.value;

                      if (
                        tempPanes[index].type === QUESTION_TYPE.COMPREHENSION
                      ) {
                        tempPanes[index].textAI = e.target.value;
                        // store the original ai text to able the user change the correct answer
                        //@ts-ignore
                        tempPanes[index].originalTextAI = e.target.value;
                      } else if (
                        (tempPanes[index].type as string) ===
                        'FILL_IN_THE_BLANKS'
                      ) {
                        tempPanes[index].textAI = e.target.value;
                        //@ts-ignore
                        tempPanes[index].originalTextAI = e.target.value;
                      } else {
                        tempPanes[index].textAI = cleanStringForAI(
                          e.target.value,
                        );
                      }

                      setPanes(tempPanes);
                    }}
                    placeholder="Question text"
                  />
                </div>
              )}
              {(QUESTION_TYPE_DICT[pane.type] === 'NORMAL_SCORE' ||
                pane.type === QUESTION_TYPE.TRANSCRIBE_AND_SCORE) && (
                <div
                  style={{
                    marginTop: '20px',
                    marginBottom: '20px',
                    border: '2px solid red',
                  }}>
                  <Text>
                    Question AI text{' '}
                    {pane.type === QUESTION_TYPE.TRANSCRIBE_AND_SCORE && (
                      <span style={{color: '#999', fontSize: '12px'}}>
                        (optional for reference)
                      </span>
                    )}
                  </Text>
                  <Input
                    value={panes[index].textAI}
                    placeholder={
                      pane.type === QUESTION_TYPE.TRANSCRIBE_AND_SCORE
                        ? 'Enter reference text for scoring (optional)'
                        : 'Question AI text'
                    }
                    onBlur={() => {
                      if (pane.type === QUESTION_TYPE.COMPREHENSION) {
                        // Check BOTH text and textAI fields for CHOICES keywords
                        const textChoices = (panes[index].text || '').split(
                          ' ',
                        );
                        const aiTextChoices = (panes[index].textAI || '').split(
                          ' ',
                        );
                        const allChoices = [...textChoices, ...aiTextChoices];

                        let newOptions = {};
                        let newChoicesCount = {};

                        // Find all CHOICES keywords from both fields
                        const numOfchoiceOptions = allChoices
                          .filter(text => text.startsWith('CHOICES'))
                          .map(text => text.replace(/[.,!?:;]+$/, '')); // Strip trailing punctuation

                        // Remove duplicates using Set
                        const uniqueChoiceOptions = [
                          ...new Set(numOfchoiceOptions),
                        ];

                        // Show alert only once if no CHOICES keywords found
                        if (uniqueChoiceOptions.length === 0) {
                          if (!shownChoicesAlerts.has(index)) {
                            alert(
                              'Must Text or AI Text Contains at least one keyword CHOICES1',
                            );
                            setShownChoicesAlerts(
                              new Set(shownChoicesAlerts).add(index),
                            );
                          }
                        } else {
                          // Remove from shown alerts if user has fixed the issue
                          if (shownChoicesAlerts.has(index)) {
                            const newAlerts = new Set(shownChoicesAlerts);
                            newAlerts.delete(index);
                            setShownChoicesAlerts(newAlerts);
                          }
                        }

                        // REMOVABLE: Debug logging
                        console.log(
                          'CHOICES keywords detected in text:',
                          textChoices.filter(t => t.startsWith('CHOICES')),
                        );
                        console.log(
                          'CHOICES keywords detected in textAI:',
                          aiTextChoices.filter(t => t.startsWith('CHOICES')),
                        );
                        console.log(
                          'All unique CHOICES keywords:',
                          uniqueChoiceOptions,
                        );

                        uniqueChoiceOptions.forEach(value => {
                          newOptions[value] = [];
                          newChoicesCount[value] = 2; // Initialize with 2 choices
                        });

                        let tempPanes = [...panes];
                        //@ts-ignore
                        tempPanes[index].customChoices = newOptions;
                        //@ts-ignore
                        tempPanes[index].choicesCount = newChoicesCount;

                        // REMOVABLE: Debug logging
                        console.log(
                          'Initialized choicesCount:',
                          newChoicesCount,
                        );

                        // Auto-select first CHOICES keyword
                        if (uniqueChoiceOptions.length > 0) {
                          setSelectChoice(uniqueChoiceOptions[0]);
                          // REMOVABLE: Debug logging
                          console.log(
                            'Auto-selected choice:',
                            uniqueChoiceOptions[0],
                          );
                        }

                        setPanes(tempPanes);
                      } else if (
                        (pane.type as string) === 'FILL_IN_THE_BLANKS'
                      ) {
                        // Check BOTH text and textAI fields for BLANK keywords
                        const textBlanks = (panes[index].text || '').split(' ');
                        const aiTextBlanks = (panes[index].textAI || '').split(
                          ' ',
                        );
                        const allBlanks = [...textBlanks, ...aiTextBlanks];

                        let newOptions = {};
                        let newBlankCount = {};

                        // Find all BLANK keywords from both fields
                        const numOfBlankOptions = allBlanks
                          .filter(text => text.startsWith('BLANK'))
                          .map(text => text.replace(/[.,!?:;]+$/, '')); // Strip trailing punctuation

                        // Remove duplicates using Set
                        const uniqueBlankOptions = [
                          ...new Set(numOfBlankOptions),
                        ];

                        // Show alert only once if no BLANK keywords found
                        if (uniqueBlankOptions.length === 0) {
                          if (!shownChoicesAlerts.has(index)) {
                            alert(
                              'Must Text or AI Text Contains at least one keyword BLANK1',
                            );
                            setShownChoicesAlerts(
                              new Set(shownChoicesAlerts).add(index),
                            );
                          }
                        } else {
                          // Remove from shown alerts if user has fixed the issue
                          if (shownChoicesAlerts.has(index)) {
                            const newAlerts = new Set(shownChoicesAlerts);
                            newAlerts.delete(index);
                            setShownChoicesAlerts(newAlerts);
                          }
                        }

                        // REMOVABLE: Debug logging
                        console.log(
                          'BLANK keywords detected in text:',
                          textBlanks.filter(t => t.startsWith('BLANK')),
                        );
                        console.log(
                          'BLANK keywords detected in textAI:',
                          aiTextBlanks.filter(t => t.startsWith('BLANK')),
                        );
                        console.log(
                          'All unique BLANK keywords:',
                          uniqueBlankOptions,
                        );

                        uniqueBlankOptions.forEach(value => {
                          newOptions[value] = [];
                          newBlankCount[value] = 2; // Initialize with 2 choices
                        });

                        let tempPanes = [...panes];
                        //@ts-ignore
                        tempPanes[index].customChoices = newOptions;
                        //@ts-ignore
                        tempPanes[index].choicesCount = newBlankCount;

                        // REMOVABLE: Debug logging
                        console.log('Initialized blankCount:', newBlankCount);

                        // Auto-select first BLANK keyword
                        if (uniqueBlankOptions.length > 0) {
                          setSelectChoice(uniqueBlankOptions[0]);
                          // REMOVABLE: Debug logging
                          console.log(
                            'Auto-selected blank:',
                            uniqueBlankOptions[0],
                          );
                        }

                        setPanes(tempPanes);
                      } else {
                        let tempPanes = [...panes];

                        //@ts-ignore
                        tempPanes[index].customChoices = {};
                        setPanes(tempPanes);
                      }
                    }}
                    onChange={e => {
                      let tempPanes = [...panes];

                      if (
                        tempPanes[index].type === QUESTION_TYPE.COMPREHENSION ||
                        (tempPanes[index].type as string) ===
                          'FILL_IN_THE_BLANKS' ||
                        tempPanes[index].type ===
                          QUESTION_TYPE.TRANSCRIBE_AND_SCORE
                      ) {
                        tempPanes[index].textAI = e.target.value;
                        // Store originalTextAI to preserve value when checking correct answers
                        //@ts-ignore
                        tempPanes[index].originalTextAI = e.target.value;
                      } else {
                        tempPanes[index].textAI = cleanStringForAI(
                          e.target.value,
                        );
                      }

                      setPanes(tempPanes);
                    }}
                  />
                </div>
              )}

              {pane.type === QUESTION_TYPE.COMPREHENSION && (
                <div>
                  <div>
                    <Text style={{display: 'block'}}>Choice Options </Text>

                    <Select
                      value={selectChoice}
                      showSearch
                      style={{width: '30%'}}
                      placeholder={'Select ' + selectChoice}
                      optionFilterProp="children"
                      onChange={(selectedChoice: string) => {
                        setSelectChoice(selectedChoice);
                        let tempPanes = [...panes];
                        setPanes(tempPanes);
                      }}>
                      {Object.keys(panes[index].customChoices || {}).map(
                        choice => (
                          <Select.Option key={choice} value={choice}>
                            {choice}
                          </Select.Option>
                        ),
                      )}
                    </Select>
                  </div>
                  {selectChoice && (
                    <div>
                      {/* set 2 choices as an initial choices  */}
                      {/* @ts-ignore */}
                      {new Array(
                        //@ts-ignore
                        panes[index]?.choicesCount?.[selectChoice] || 2,
                      )
                        .fill(null)
                        .map((_, arrayIndex) => {
                          const pane = panes[index];
                          const customChoices =
                            pane?.customChoices?.[selectChoice] || [];
                          return (
                            <div
                              key={arrayIndex}
                              style={{marginTop: '20px', marginBottom: '20px'}}>
                              <Row align="middle">
                                <Text>
                                  Choice number {arrayIndex + 1}
                                  <Checkbox
                                    style={{marginLeft: '10px'}}
                                    checked={
                                      //@ts-ignore
                                      pane?.correctIndex?.[selectChoice] ===
                                      arrayIndex
                                    }
                                    onChange={() => {
                                      if (!pane) return;
                                      let tempPanes = [...panes];

                                      // Initialize correctIndex if needed
                                      //@ts-ignore
                                      if (!tempPanes[index].correctIndex) {
                                        //@ts-ignore
                                        tempPanes[index].correctIndex = {};
                                      }

                                      // Set correct answer index
                                      //@ts-ignore
                                      tempPanes[index].correctIndex[
                                        selectChoice
                                      ] = arrayIndex;

                                      // Get original text (with CHOICES keywords)
                                      //@ts-ignore
                                      const originalText =
                                        tempPanes[index].originalTextAI ||
                                        tempPanes[index].text ||
                                        '';

                                      // Build blanks array from customChoices
                                      //@ts-ignore
                                      const blanks = Object.keys(
                                        tempPanes[index].correctIndex,
                                      ).map(choiceKey => {
                                        //@ts-ignore
                                        const correctIndex =
                                          tempPanes[index].correctIndex[
                                            choiceKey
                                          ];
                                        const correctAnswer =
                                          tempPanes[index].customChoices[
                                            choiceKey
                                          ]?.[correctIndex] || choiceKey;

                                        return {
                                          name: choiceKey,
                                          choices:
                                            tempPanes[index].customChoices[
                                              choiceKey
                                            ] || [],
                                          correctAnswer: correctAnswer,
                                        };
                                      });

                                      // Update text and textAI based on question type
                                      if (
                                        (tempPanes[index].type as string) ===
                                        'FILL_IN_THE_BLANKS'
                                      ) {
                                        // For FILL_IN_THE_BLANKS, use BLANK keyword replacement
                                        tempPanes[index].text =
                                          duplicateBlankKeywordsByWordCount(
                                            originalText,
                                            blanks,
                                          );
                                        tempPanes[index].textAI =
                                          replaceBlankKeywordsInText(
                                            originalText,
                                            blanks,
                                          );
                                      } else {
                                        // For COMPREHENSION, use CHOICES keyword replacement
                                        tempPanes[index].text =
                                          duplicateChoicesKeywordsByWordCount(
                                            originalText,
                                            blanks,
                                          );
                                        tempPanes[index].textAI =
                                          replaceChoicesInText(
                                            originalText,
                                            blanks,
                                          );
                                      }

                                      setPanes(tempPanes);
                                    }}
                                  />{' '}
                                  Correct answer
                                </Text>
                              </Row>
                              <Input
                                value={customChoices[arrayIndex] || ''}
                                onChange={e => {
                                  if (!pane) return;
                                  let tempPanes = [...panes];
                                  const currentChoices =
                                    tempPanes[index].customChoices[
                                      selectChoice
                                    ] || [];
                                  currentChoices[arrayIndex] = e.target.value;

                                  tempPanes[index].customChoices[selectChoice] =
                                    currentChoices;
                                  setPanes(tempPanes);
                                }}
                                placeholder={`Choice number ${arrayIndex + 1}`}
                              />
                            </div>
                          );
                        })}
                      <Button
                        type="dashed"
                        onClick={() => {
                          let tempPanes = [...panes];

                          let currentCount =
                            //@ts-ignore
                            tempPanes[index].choicesCount[selectChoice] || 2;

                          if (currentCount < 4) {
                            //@ts-ignore
                            tempPanes[index].choicesCount[selectChoice] =
                              currentCount + 1;
                            setPanes(tempPanes);
                          }
                        }}
                        //@ts-ignore
                        disabled={panes[index].choicesCount[selectChoice] >= 4}
                        style={{marginTop: '10px'}}>
                        Add Choice
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {/* REARRANGE_SENTENCE Question Editor */}
              {(pane.type as string) === 'REARRANGE_SENTENCE' && (
                <RearrangeSentenceEditor
                  question={panes[index]}
                  onAITextChange={newText => {
                    let tempPanes = [...panes];

                    tempPanes[index].textAI = newText;

                    // FIX: Properly split words so last word is not broken
                    const words =
                      newText
                        ?.trim()
                        .split(' ')
                        .map((w: string) => w.trim())
                        .filter((w: string) => w.length > 0) || [];

                    tempPanes[index].payload = {
                      ...tempPanes[index].payload,
                      words,
                      shuffledOrder: [...words].sort(() => Math.random() - 0.5),
                      correctOrder: words.map((_: any, i: number) => i),
                    };
                    tempPanes[index].text =
                      tempPanes[index].payload.shuffledOrder.join(' ');

                    setPanes(tempPanes);
                  }}
                  onShuffledOrderChange={shuffledWords => {
                    let tempPanes = [...panes];

                    // FIX: Properly split words
                    const words =
                      panes[index].textAI
                        ?.trim()
                        .split(' ')
                        .map((w: string) => w.trim())
                        .filter((w: string) => w.length > 0) || [];

                    tempPanes[index].payload = {
                      ...tempPanes[index].payload,
                      words,
                      shuffledOrder: shuffledWords,
                      correctOrder: words.map((_: any, i: number) => i),
                    };
                    tempPanes[index].text = shuffledWords.join(' ');

                    setPanes(tempPanes);
                  }}
                />
              )}

              {/* FILL_IN_THE_BLANKS uses same structure as COMPREHENSION */}
              {(pane.type as string) === 'FILL_IN_THE_BLANKS' && (
                <div>
                  <Text
                    style={{
                      display: 'block',
                      marginBottom: '10px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}>
                    ℹ️ FILL_IN_THE_BLANKS - Similar to COMPREHENSION
                  </Text>

                  <Text
                    style={{
                      display: 'block',
                      marginBottom: '20px',
                      fontSize: '12px',
                      color: '#666',
                    }}>
                    Use BLANK1, BLANK2, etc. in the question text where students
                    need to fill in blanks.
                  </Text>

                  <div>
                    <Text style={{display: 'block'}}>Choice Options </Text>

                    <Select
                      value={selectChoice}
                      showSearch
                      style={{width: '30%'}}
                      placeholder={'Select ' + selectChoice}
                      optionFilterProp="children"
                      onChange={(selectedChoice: string) => {
                        setSelectChoice(selectedChoice);

                        let tempPanes = [...panes];
                        setPanes(tempPanes);
                      }}>
                      {Object.keys(panes[index].customChoices).map(choice => (
                        <Select.Option key={choice} value={choice}>
                          {choice}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>

                  {selectChoice && (
                    <div>
                      <div
                        style={{
                          marginTop: '20px',
                          marginBottom: '20px',
                        }}>
                        <Text>Correct answer for {selectChoice}</Text>
                        <Input
                          value={
                            panes[index]?.customChoices?.[selectChoice]?.[0] ||
                            ''
                          }
                          onChange={e => {
                            let tempPanes = [...panes];

                            if (!tempPanes[index].customChoices) {
                              tempPanes[index].customChoices = {};
                            }
                            if (!tempPanes[index].correctIndex) {
                              tempPanes[index].correctIndex = {};
                            }

                            tempPanes[index].customChoices[selectChoice] = [
                              e.target.value,
                            ];
                            tempPanes[index].choicesCount = {
                              ...tempPanes[index].choicesCount,
                              [selectChoice]: 1,
                            };
                            tempPanes[index].correctIndex[selectChoice] = 0;

                            updateFillBlankDerivedFields(tempPanes, index);
                            setPanes(tempPanes);
                          }}
                          placeholder={`Correct answer for ${selectChoice}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div>
                <Text style={{fontSize: '20', fontWeight: 'bold'}}>
                  Question asset
                </Text>
                <CreateAssetBlock
                  defaultAssetType={AssetType.PHOTO}
                  asset={panes[index].asset}
                  // @ts-ignore TODO be checked
                  onChange={asset => {
                    console.log('🎯 CreateAssetBlock onChange:', {
                      assetType: asset.type,
                      questionType: panes[index].type,
                      index,
                    });

                    // Update summary and description when asset type changes
                    const staticValues = getQuestionStaticValues(
                      panes[index].type,
                      asset.type || 'default',
                    );

                    // Create a new array with the updated pane object (deep copy of the specific pane)
                    const tempPanes = [...panes];
                    tempPanes[index] = {
                      ...panes[index],
                      asset: asset,
                      summary: staticValues.summary,
                      description: staticValues.description,
                    };

                    setPanes(tempPanes);
                    console.log(
                      '✅ Updated asset to:',
                      asset,
                      'with summary:',
                      staticValues.summary,
                    );
                  }}
                />
              </div>
              {QUESTION_TYPE_DICT[pane.type] === 'NORMAL_SCORE' && (
                <>
                  <div>
                    <Text style={{fontSize: '20', fontWeight: 'bold'}}>
                      Question correct answer asset file url
                    </Text>

                    {panes[index]?.correctAsset?.fileUri ? (
                      <Row
                        style={{
                          display: 'flex',
                          // justifyContent: 'center',
                          alignItems: 'center',
                          alignContent: 'center',
                        }}>
                        <Input
                          style={{width: '80%'}}
                          value={panes[index]?.correctAsset?.fileUri}
                          onChange={e => {}}
                          placeholder="Correct answer url"
                        />

                        <CloseCircleFilled
                          style={{marginInline: 20}}
                          onClick={() => {
                            setFileAudio(null);

                            let tempAsset = {
                              fileUri: '',
                              type: AssetType.AUDIO,
                              thumbnailUri: '',
                            };
                            let tempPanes = [...panes];
                            // @ts-ignore
                            tempPanes[index].correctAsset = tempAsset;
                            setPanes(tempPanes);
                          }}
                        />
                      </Row>
                    ) : (
                      <Row>
                        <Upload {...propsAudioUpload}>
                          <Button
                            disabled={fileAudio ? true : false}
                            icon={<UploadOutlined />}>
                            Select File
                          </Button>
                        </Upload>

                        <Button
                          type="primary"
                          onClick={() =>
                            handleUpload(
                              fileAudio,
                              UploadFileType.TEST_MEDIA,
                              index,
                            )
                          }
                          disabled={!fileAudio}
                          loading={uploading}>
                          {uploading ? 'Uploading' : 'Upload'}
                        </Button>
                      </Row>
                    )}
                  </div>
                </>
              )}
            </TabPane>
          ))}
        </Tabs>

        <Button
          onClick={async () => {
            // setIsLoading(true);
            let placementTest: TestDraft & {type?: string} = {
              questions: panes,
              name: placementTestName,
              description: placementTestDescription,
              type: placementTestType || undefined,
              asset: placementTestAsset!,
              duration,
              startDate,
              expirationDate,
            };
            console.log(placementTest);

            // @ts-ignore
            let placementTestValidationResult =
              validateCreateTest(placementTest);
            if (placementTestValidationResult?.status) {
              const levelDeterminationValidation =
                validateLevelDeterminationQuestions();

              if (!levelDeterminationValidation.isValid) {
                alert(
                  'LEVEL_DETERMINATION placement tests can only use these question types:\n' +
                    getLevelDeterminationAllowedQuestionTypesText() +
                    '\n\nInvalid questions:\n' +
                    levelDeterminationValidation.errors.join('\n'),
                );
                return;
              }

              try {
                const response =
                  await placementTestService.createPlacementTest(placementTest);
                alert('Placement Test with ID: ' + response?.id + ' created');
                history.push('/placement-tests');
              } catch (e) {
                setIsLoading(false);
                alert('Error creating placement test');
              }
            } else {
              alert(placementTestValidationResult.message);
              setIsLoading(false);
            }
          }}>
          Create
        </Button>
      </div>
      <Modal
        okButtonProps={{hidden: true}}
        cancelButtonProps={{hidden: true}}
        visible={isLoading}
        closable={true}>
        <div>
          <Spin size="large" style={{margin: 'auto', width: '100%'}} />
          <p style={{textAlign: 'center'}}>Loading...</p>
        </div>
      </Modal>
    </>
  );
};
